import { v4 } from 'uuid';

import makeApi from './api';

export enum ChatItemType {
  FORM = 'form',
  MESSAGE = 'message',
  DISCLAIMER_MESSAGE = 'disclaimer_message',
  TWILIO_CREDS = 'twilio_credentials',
  HIDE_PREV_MESSAGES = 'hide_previous_messages',
  TRANSFER_TO_LIVECHAT = 'transfer_to_livechat',
  TRANSFER_TO_VA = 'transfer_to_virtualassistant',
  DISABLE_TYPING_AREA = 'disable_typing_area',
  ENABLE_TYPING_AREA = 'enable_typing_area',
  REDIRECT_LINK = 'redirect_link',
  ANALYTICS_TAG = 'analytics_tag',
  CONTACT_FORM = 'contact_form',
  LOCATION_RESULTS = 'location_results',
  KB_ENTRY = 'kb_entry',
}

export enum ChatItemSourceType {
  SYSTEM = 'system',
  PATIENT = 'patient',
  NURSE = 'nurse',
  BOT = 'bot',
  SERVICE = 'service',
  ANALYTICS = 'analytics',
}

export enum ChatFormItemType {
  SINGE_CHOICE = 'single_choice',
  TEXT = 'text',
  INPUT = 'input',
}

export interface BaseChatItem {
  type: ChatItemType;
  source: string;
  delay_duration: number;
  visible: boolean;
  aiResponse: boolean;
  uuid: string;
  errorMessage?: boolean;
}

export interface ChatItemMessage extends BaseChatItem {
  type: ChatItemType.MESSAGE;
  message: string;
}

export enum AnalyticsTagType {
  CHANGE = 'change',
  DISMISS = 'dismiss',
  ERROR = 'error',
  VIEW = 'view',
}

export enum AnalyticsTagOperator {
  ANY = 'any',
  EXACT = 'exact',
  IS_SET = 'is_set',
}

export interface AnalyticsTag {
  name: string;
  posthog_event_config: {
    type: AnalyticsTagType;
    properties: {
      operator: AnalyticsTagOperator;
      value: string;
    }[];
  };
  tag_pattern_id: string;
}

export interface ChatFormItem {
  item_type: ChatFormItemType;
  choices: string[];
  ref_id: string;
  placeholder: string;
  errors?: string[];
  value?: string;
  value_role?: string;
  analytics_tags?: AnalyticsTag[];
}

export enum ChatItemFormType {
  CONTACT_FORM = 'external_contact_forms',
}

export interface ChatItemForm extends BaseChatItem {
  type: ChatItemType.FORM;
  items: ChatFormItem[];
  ref_id: string;
  form_type?: ChatItemFormType;
  ref_slug: string;
}

export interface ChatItemRedirect extends BaseChatItem {
  open_in_new_tab: boolean;
  url: string;
}

export enum KBEntryViewType {
  SOURCE = 'source',
  CARD = 'card',
}

export interface ChatItemKBEntry extends BaseChatItem {
  type: ChatItemType.KB_ENTRY;
  kb_constant_id: string;
  title: string;
  description: string;
  view_type: KBEntryViewType;
}

export interface ChatItemAnalyticsTag extends BaseChatItem {
  type: ChatItemType.ANALYTICS_TAG;
  event_type: string;
  tag_id: string;
  tag_name: string;
  posthog_event_config?: string;
}

export interface LocationResult {
  place_id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  distance: string;
  phone_number: string;
  open_now?: boolean;
  opens_at?: string | null;
  closes_at?: string | null;
  full_hours?: string[];
}

export interface ChatItemLocationResults extends BaseChatItem {
  items: LocationResult[];
  origin_zip: string;
}

export type ChatItem = (
  | ChatItemMessage
  | ChatItemForm
  | ChatItemRedirect
  | ChatItemAnalyticsTag
  | ChatItemLocationResults
  | ChatItemKBEntry
) & {
  userMessage?: boolean;
};
export type AnswerItem = ChatItemForm;

export interface ChatBotResponse {
  chat_items: ChatItem[];
  form?: ChatItemForm;
}

export interface ChatBotFormAnswer {
  form_ref_id: string;
  item_ref_id: string;
  value?: string;
}

export interface ChatBotUserMessage {
  content: string;
}

interface ApiResponse {
  chat_items: unknown[];
  [key: string]: unknown;
}

async function makeRequestWithRetry<T, R>(
  url: string,
  data: T,
  api: ReturnType<typeof makeApi>,
  processResponse: (responseData: ApiResponse) => R
): Promise<R> {
  async function executeRequest(retryCount = 0): Promise<R> {
    try {
      const response = await api.post(url, data, { timeout: 10000 });
      return processResponse(response.data);
    } catch (error) {
      if (retryCount < 1) {
        return executeRequest(retryCount + 1);
      }
      throw error;
    }
  }

  return executeRequest();
}

export async function startChatBot({ uuid, action }: { uuid?: string; action?: string }): Promise<ChatBotResponse> {
  const url = `/api/v1/web_chat/v3.0/help-center/${uuid}/virtual-assistant/`;
  const api = makeApi();
  const response = await api.post(url, {
    ...(action ? { action } : {}),
  });
  return {
    ...response.data,
    chat_items: response.data.chat_items.map((item: ChatItem) => ({
      ...item,
      delay_duration: item.delay_duration * 1000,
      uuid: v4(),
      visible: false,
      aiResponse: false,
    })),
  };
}

export async function sendAnswer({ uuid, data }: { uuid: string; data: ChatBotFormAnswer }): Promise<ChatBotResponse> {
  const url = `/api/v1/web_chat/v3.0/help-center/${uuid}/virtual-assistant/answer/`;
  const api = makeApi();

  return makeRequestWithRetry(url, data, api, (responseData) => ({
    ...responseData,
    chat_items: responseData.chat_items.map((item) => {
      const chatItem = item as unknown as ChatItem;
      return {
        ...chatItem,
        delay_duration: chatItem.delay_duration * 1000,
        uuid: v4(),
        visible: false,
        aiResponse: false,
      };
    }),
    form: responseData.form as ChatItemForm | undefined,
  }));
}

export async function sendMessage({
  uuid,
  data,
}: {
  uuid: string;
  data: ChatBotUserMessage;
}): Promise<ChatBotResponse> {
  const url = `/api/v1/web_chat/v3.0/help-center/${uuid}/virtual-assistant/message/`;
  const api = makeApi();

  return makeRequestWithRetry(url, data, api, (responseData) => ({
    ...responseData,
    chat_items: responseData.chat_items.map((item) => {
      const chatItem = item as unknown as ChatItem;
      return {
        ...chatItem,
        delay_duration: chatItem.delay_duration * 1000,
        uuid: v4(),
        visible: false,
        aiResponse: true,
      };
    }),
    form: responseData.form as ChatItemForm | undefined,
  }));
}

export async function dismissQuestion({ uuid, refId }: { uuid: string; refId: string }): Promise<ChatBotResponse> {
  const url = `/api/v1/web_chat/v3.0/help-center/${uuid}/virtual-assistant/dismiss/`;
  const api = makeApi();
  const response = await api.post(url, { form_ref_id: refId });
  return {
    ...response.data,
    chat_items: response.data.chat_items.map((item: ChatItem) => ({
      ...item,
      delay_duration: item.delay_duration * 1000,
      uuid: v4(),
      visible: false,
      aiResponse: false,
    })),
    form: response.data.form as ChatItemForm | undefined,
  };
}
